import { Box, Stack, Typography } from '@mui/material';
import { VyLayout } from '../../components/new-design/layout/VyLayout';
import { Grid } from '@mui/material';

import { TopKPIMetrics } from './components/TopKPIMetrics/TopKPIMetrics';
import type { KPIMetric } from './components/TopKPIMetrics/TopKPIMetrics';
import { RevenueTrendsAnalyticsCard } from './components/analytics/RevenueTrendsAnalyticsCard/RevenueTrendsAnalyticsCard';
import { InvoicesAnalyticsCard } from './components/analytics/InvoicesAnalyticsCard/InvoicesAnalyticsCard';
import { ArHealthAnalyticsCard } from './components/analytics/ArHealthAnalyticsCard/ArHealthAnalyticsCard';
import { Helmet } from 'react-helmet';
import { formatPriceToAbbreviated } from '../../utils/formatters/formatPriceToAbbreviated.ts';
import { DEFAULT_CURRENCY } from '@vy/types';
import { MRRWaterfallAnalyticsCard } from './components/analytics/MRRWaterfallAnalyticsCard/MRRWaterfallAnalyticsCard.tsx';
import { HomeQuickActions } from './components/QuickActions/HomeQuickActions.tsx';
import { useHomePageAccountSummary } from '../../hooks/requests/pages/use-home-page-account-summary';
import { useMemo } from 'react';
import { isNullOrUndefined } from '@vy/utils';
import { useAiCopilot } from '../../components/ai-copilot/context/use-ai-copilot';
import { homePageWidgetsSelectors } from '@vy/app-selectors';

const acvFormatter = (value: number) => formatPriceToAbbreviated(DEFAULT_CURRENCY, value);

const buildKpiMetrics = (
  kpiMetrics: NonNullable<ReturnType<typeof useHomePageAccountSummary>['data']>['kpiMetrics'],
): KPIMetric[] => [
  {
    name: 'ACV (12 month avg)',
    value: kpiMetrics.acv,
    prevValue: kpiMetrics.previousAcv,
    formatter: acvFormatter,
    dataVy: homePageWidgetsSelectors.topKpiAcv,
  },
  {
    name: 'Active Customers',
    value: kpiMetrics.activeCustomers,
    prevValue: kpiMetrics.previousActiveCustomers,
    dataVy: homePageWidgetsSelectors.topKpiActiveCustomers,
  },
  {
    name: 'Active Contracts',
    value: kpiMetrics.activeContracts,
    prevValue: kpiMetrics.previousActiveContracts,
    dataVy: homePageWidgetsSelectors.topKpiActiveContracts,
  },
  {
    name: 'Renewal Contracts',
    value: kpiMetrics.renewalContracts,
    dataVy: homePageWidgetsSelectors.topKpiRenewalContracts,
  },
  {
    name: 'Pending Contracts',
    value: kpiMetrics.inReviewContracts,
    navigationProps: {
      to: '/plans?contractStatuses=InReview',
      label: 'View contracts',
    },
    dataVy: homePageWidgetsSelectors.topKpiPendingContracts,
  },
];

const HomeLayout = () => {
  const { isOpen, drawerWidth } = useAiCopilot();
  const { data: accountSummary, isLoading: isAccountSummaryLoading } = useHomePageAccountSummary({
    suspense: false,
  });

  const metrics = useMemo(() => {
    const kpiMetrics = accountSummary?.kpiMetrics;

    if (isNullOrUndefined(kpiMetrics)) {
      return [];
    }

    return buildKpiMetrics(kpiMetrics);
  }, [accountSummary?.kpiMetrics]);

  return (
    <>
      <Box
        sx={{
          transition: (theme) =>
            theme.transitions.create('margin-right', {
              easing: isOpen
                ? theme.transitions.easing.easeOut
                : theme.transitions.easing.sharp,
              duration: isOpen
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
          marginRight: isOpen ? `${drawerWidth}px` : 0,
        }}
      >
        <VyLayout
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
            height: '100%',
            overflowX: 'hidden',
          }}
        >
          <Stack
            sx={{
              gap: 2,
              padding: "24px",
              paddingTop: "12px"
            }}>
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center"
              }}>
              <Typography variant="h5">Business Overview</Typography>
              <HomeQuickActions />
            </Stack>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TopKPIMetrics metrics={metrics} isLoading={isAccountSummaryLoading} />
              </Grid>

              <Grid size={{ lg: 6, md: 6, xs: 12 }}>
                <MRRWaterfallAnalyticsCard
                  waterfall={accountSummary?.kpiMetrics?.mrrWaterfall}
                  isLoading={isAccountSummaryLoading}
                />
              </Grid>

              <Grid size={{ lg: 6, md: 12, xs: 12 }}>
                <RevenueTrendsAnalyticsCard />
              </Grid>

              <Grid size={{ lg: 6, md: 12, xs: 12 }}>
                <ArHealthAnalyticsCard />
              </Grid>

              <Grid size={{ lg: 6, md: 12, xs: 12 }}>
                <InvoicesAnalyticsCard />
              </Grid>
            </Grid>
          </Stack>
        </VyLayout>
      </Box>
    </>
  );
};

export const Home = () => {
  return (
    <>
      <Helmet>
        <title>Business Overview</title>
      </Helmet>
      <HomeLayout />
    </>
  );
};
